/* eslint-disable no-param-reassign */
import React, { useMemo, useCallback, useRef, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  ColDef,
  GetRowIdParams,
  ICellRendererParams,
  CellValueChangedEvent,
  ValueSetterParams,
  EditableCallbackParams,
  ProcessCellForExportParams
} from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { CustomReportVariable, ThresholdValueSetting } from '../../../../../redux/context/customReports/typesVariable'
import { DisplayScale } from '../../../../../shared/utils/numberScale'
import { GraphType } from '../chart/ChartTypeSelect'
import { DashStyle } from '../../../../../redux/context/customReports/typesCategory'
import LineIcon from '../../../../../components/Icons/LineIcons/LineIcon'
import { ChartYAxis } from '../chart/types'
import { SectionType } from '../../../../../redux/context/customReports/typesSection'
import { isFieldDisabled } from '../../../../../redux/context/customReports/utils'
import { ContractProduct } from '../../../../../types/contract/Contract'
import { useAuthority } from '../../../../../utils/Authorizable/authorize'
import { contextCompanyIdSelector } from '../../../../../redux/context/company/selectors'
import CustomAgGrid from '../../../../../shared/components/custom/Grid/CustomAgGrid'
import { NumericCellEditor } from '../../../../../shared/components/custom/Grid/Editor'

interface ParamsAndStylesTableAgGridProps {
  sectionType: SectionType
  variables: CustomReportVariable[]
  onChange: (variables: CustomReportVariable[]) => void
}

export interface ParamsAndStylesTableHandle {
  stopEditing: () => void
}

const INDENT_LEVELS = Array.from({ length: 9 }, (_, i) => ({ key: i, value: i, label: i.toString() }))
const DECIMAL_OPTIONS = Array.from({ length: 4 }, (_, i) => ({ key: i, value: i, label: i.toString() }))

// Stable fallback ID generator for rows without id
const fallbackIdMap = new WeakMap<CustomReportVariable, string>()
let fallbackIdCounter = 0

// Cell Renderer
const RowTypeCellRenderer: React.FC<ICellRendererParams<CustomReportVariable>> = ({ data }) => {
  const { t } = useTranslation()
  if (!data) return null
  return <span>{t(`variableRowType:${data.rowType}`)}</span>
}

const ParamsAndStylesTableAgGrid = forwardRef<ParamsAndStylesTableHandle, ParamsAndStylesTableAgGridProps>(
  ({ sectionType, variables, onChange }, ref) => {
    const { t } = useTranslation()
    const isPro = useAuthority({ product: ContractProduct.PRO })
    const companyId = useSelector(contextCompanyIdSelector)
    const gridRef = useRef<AgGridReact>(null)

    useImperativeHandle(ref, () => ({
      stopEditing: () => gridRef.current?.api?.stopEditing()
    }))

    const popupParent = useMemo<HTMLElement>(() => document.body, [])

    const thresholdOptions = useMemo(
      () =>
        Object.keys(ThresholdValueSetting).map(val => ({
          label: t(`customReportPage:${val}`),
          value: val
        })),
      [t]
    )

    const displayScaleOptions = useMemo(
      () =>
        Object.values(DisplayScale).map(value => ({
          key: value,
          value,
          label: t(`displayScale:${value}`)
        })),
      [t]
    )

    const yAxisOptions = useMemo(
      () => [
        { label: t('customReportPage:leftAxis'), value: ChartYAxis.left },
        { label: t('customReportPage:rightAxis'), value: ChartYAxis.right }
      ],
      [t]
    )

    const dashStyleOptions = useMemo(
      () => Object.values(DashStyle).map(value => ({ label: <LineIcon type={value as DashStyle} />, value })),
      []
    )

    const graphTypeOptions = useMemo(
      () => Object.values(GraphType).map(value => ({ label: t(`graphType:${value}`), value })),
      [t]
    )

    // Helper to update variables on cell value change
    const onCellValueChanged = useCallback(
      (event: CellValueChangedEvent<CustomReportVariable>) => {
        const { data } = event

        if (!data) return

        // Safe check when id is missing
        if (data.id == null) {
          console.warn('Cannot update variable: data.id is missing')
          return
        }

        // Find the variable by identity instead of rowIndex (which breaks when sorted/filtered)
        const foundIndex = variables.findIndex(v => v.id === data.id)
        if (foundIndex === -1) {
          console.warn('Cannot update variable: not found in variables array', data.id)
          return
        }

        // Create a new array with the updated variable
        const newVariables = [...variables]
        newVariables[foundIndex] = data

        // Call the onChange callback with the updated variables
        onChange(newVariables)
      },
      [variables, onChange]
    )

    // Editable function to check if cell should be editable
    const isEditable = useCallback(
      (params: EditableCallbackParams<CustomReportVariable>, fieldName: string) => {
        if (!params.data) return false
        return !isFieldDisabled(params.data, sectionType, fieldName as any)
      },
      [sectionType]
    )

    // Value setter for checkbox fields with transformations
    const fontStyleValueSetter = useCallback((params: ValueSetterParams<CustomReportVariable>) => {
      if (params.data) {
        const newValue = params.newValue ? 'italic' : 'normal'
        if (!params.data.style) {
          params.data.style = {} as any
        }
        if (params.data.style) {
          params.data.style.fontStyle = newValue as 'italic' | 'normal'
        }
        return true
      }
      return false
    }, [])

    const expandableValueSetter = useCallback((params: ValueSetterParams<CustomReportVariable>) => {
      if (params.data) {
        if (!params.data.params) params.data.params = {}
        params.data.params.expandable = params.newValue !== false
        return true
      }
      return false
    }, [])

    // Base columns (Type and Name) - pinned to left
    const baseColumns = useMemo<ColDef<CustomReportVariable>[]>(
      () => [
        {
          headerName: t('global:type'),
          field: 'rowType',
          width: 180,
          pinned: 'left',
          lockPosition: 'left',
          suppressMovable: true,
          cellRenderer: RowTypeCellRenderer,
          editable: false
        },
        {
          headerName: t('global:name'),
          field: 'name',
          width: 180,
          pinned: 'left',
          lockPosition: 'left',
          suppressMovable: true,
          editable: params => isEditable(params, 'name'),
          cellEditor: 'agTextCellEditor',
          cellEditorParams: {
            maxLength: 255
          }
        }
      ],
      [t, isEditable]
    )

    // Common columns across all section types
    const commonColumns = useMemo<ColDef<CustomReportVariable>[]>(
      () => [
        {
          headerName: t('global:dataType'),
          field: 'params.dataType' as any,
          width: 150,
          type: 'scenarioCascader',
          editable: params => isEditable(params, 'dataType'),
          cellEditorParams: {
            companyId
          }
        },
        {
          headerName: t('investmentsPage:dimensionId'),
          field: 'params.dimension.dimensionId' as any,
          width: 100,
          hide: !isPro,
          type: 'dimensionTreeSelect',
          editable: params => isEditable(params, 'dimension'),
          cellEditorParams: {
            companyId
          },
          valueGetter: params => {
            return params.data?.params?.dimension?.dimensionId
          },
          valueSetter: params => {
            if (!params.data.params) {
              params.data.params = {}
            }
            if (!params.data.params.dimension) {
              params.data.params.dimension = {}
            }
            params.data.params.dimension.dimensionId = params.newValue
            return true
          }
        },
        {
          headerName: t('global:monthOffset'),
          field: 'params.offset' as any,
          width: 80,
          editable: params => isEditable(params, 'offset'),
          cellEditor: NumericCellEditor,
          cellClass: 'ag-right-aligned-cell',
          cellEditorParams: {
            allowDecimals: false,
            allowNegative: true
          }
        },
        {
          headerName: t('displayScale:label'),
          field: 'style.numberFormat.displayScale' as any,
          width: 110,
          type: 'select',
          editable: params => isEditable(params, 'displayScale'),
          cellEditorParams: {
            options: displayScaleOptions
          },
          valueGetter: params => params.data?.style?.numberFormat?.displayScale ?? DisplayScale.default,
          valueSetter: params => {
            if (!params.data) return false
            if (!params.data.style) {
              params.data.style = {} as any
            }
            if (!params.data.style!.numberFormat) {
              params.data.style!.numberFormat = {}
            }
            params.data.style!.numberFormat.displayScale = params.newValue || DisplayScale.default
            return true
          }
        },
        {
          headerName: 'k/M',
          headerTooltip: t('displayScale:showScaleSuffix'),
          field: 'style.numberFormat.showScaleSuffix' as any,
          width: 36,
          cellDataType: 'boolean',
          editable: params => isEditable(params, 'showScaleSuffix'),
          valueGetter: params => params.data?.style?.numberFormat?.showScaleSuffix !== false,
          valueSetter: params => {
            if (!params.data) return false
            if (!params.data.style) {
              params.data.style = {} as any
            }
            if (!params.data.style!.numberFormat) {
              params.data.style!.numberFormat = {}
            }
            params.data.style!.numberFormat.showScaleSuffix = params.newValue !== false
            return true
          }
        },
        {
          headerName: t('global:decimal'),
          field: 'style.numberFormat.decimals' as any,
          width: 70,
          type: 'select',
          editable: params => isEditable(params, 'decimals'),
          cellEditorParams: {
            options: DECIMAL_OPTIONS
          },
          valueGetter: params => params.data?.style?.numberFormat?.decimals,
          valueSetter: params => {
            if (!params.data) return false
            if (!params.data.style) {
              params.data.style = {} as any
            }
            if (!params.data.style!.numberFormat) {
              params.data.style!.numberFormat = {}
            }
            params.data.style!.numberFormat.decimals = params.newValue
            return true
          }
        },
        {
          headerName: 'B',
          headerTooltip: t('global:bold'),
          field: 'style.fontWeight' as any,
          width: 32,
          cellDataType: 'boolean',
          editable: params => isEditable(params, 'fontWeight'),
          valueGetter: params => params.data?.style?.fontWeight === 'bold',
          valueSetter: params => {
            if (params.data) {
              if (!params.data.style) {
                params.data.style = {} as any
              }
              if (params.data.style) {
                params.data.style.fontWeight = params.newValue ? 'bold' : undefined
              }
              return true
            }
            return false
          }
        },
        {
          headerName: 'I',
          headerTooltip: t('global:italic'),
          field: 'style.fontStyle' as any,
          width: 32,
          cellDataType: 'boolean',
          editable: params => isEditable(params, 'fontStyle'),
          valueGetter: params => params.data?.style?.fontStyle === 'italic',
          valueSetter: fontStyleValueSetter
        },
        {
          headerName: '👁',
          headerTooltip: t('customReportPage:showEmptyRows'),
          field: 'params.showEmptyRows' as any,
          width: 32,
          cellDataType: 'boolean',
          editable: params => isEditable(params, 'showEmptyRows'),
          valueGetter: params => !!params.data?.params?.showEmptyRows
        }
      ],
      [t, companyId, isPro, isEditable, fontStyleValueSetter, displayScaleOptions]
    )

    // Table-specific columns
    const tableColumns = useMemo<ColDef<CustomReportVariable>[]>(
      () => [
        {
          headerName: t('customReportPage:expandable'),
          field: 'params.expandable' as any,
          width: 90,
          cellDataType: 'boolean',
          editable: params => isEditable(params, 'expandable'),
          valueGetter: params => params.data?.params?.expandable !== false,
          valueSetter: expandableValueSetter
        },
        {
          headerName: t('customReportPage:indentLevel'),
          field: 'style.indentLevel' as any,
          width: 70,
          type: 'select',
          editable: params => isEditable(params, 'indentLevel'),
          cellEditorParams: {
            options: INDENT_LEVELS
          }
        },
        {
          headerName: t('customReportPage:thresholdValueSetting'),
          field: 'style.thresholdValueSetting' as any,
          width: 200,
          type: 'select',
          editable: params => isEditable(params, 'thresholdValueSetting'),
          cellEditorParams: {
            options: thresholdOptions
          }
        },
        {
          headerName: t('global:color'),
          field: 'style.color' as any,
          width: 70,
          type: 'color',
          editable: params => {
            if (sectionType === 'pie' || sectionType === 'table') {
              return isEditable(params, 'color')
            }
            return isEditable(params, 'seriesStyleFields')
          }
        }
      ],
      [t, thresholdOptions, sectionType, isEditable, expandableValueSetter]
    )

    // Chart-specific columns
    const chartColumns = useMemo<ColDef<CustomReportVariable>[]>(
      () => [
        {
          headerName: t('customReportPage:graphType'),
          field: 'style.type' as any,
          width: 100,
          type: 'select',
          editable: params => isEditable(params, 'seriesStyleFields'),
          cellEditorParams: {
            options: graphTypeOptions,
            allowClear: true
          }
        },
        {
          headerName: t('customReportPage:dashStyle'),
          field: 'style.dashStyle' as any,
          width: 80,
          type: 'select',
          editable: params => isEditable(params, 'seriesStyleFields'),
          cellEditorParams: {
            options: dashStyleOptions
          },
          cellRendererParams: {
            customRenderer: (value: DashStyle) => (value ? <LineIcon type={value} /> : null)
          }
        },
        {
          headerName: t('customReportPage:zIndex'),
          field: 'style.zIndex' as any,
          width: 50,
          editable: params => isEditable(params, 'seriesStyleFields'),
          cellEditor: 'agNumberCellEditor',
          cellEditorParams: {
            precision: 0
          }
        },
        {
          headerName: t('global:color'),
          field: 'style.color' as any,
          width: 60,
          type: 'color',
          editable: params => isEditable(params, 'seriesStyleFields')
        },
        {
          headerName: t('global:yAxis'),
          field: 'style.yAxis' as any,
          width: 80,
          type: 'select',
          editable: params => isEditable(params, 'seriesStyleFields'),
          cellEditorParams: {
            options: yAxisOptions
          }
        }
      ],
      [t, yAxisOptions, dashStyleOptions, graphTypeOptions, isEditable]
    )

    // Pie-specific columns
    const pieColumns = useMemo<ColDef<CustomReportVariable>[]>(
      () => [
        {
          headerName: t('global:color'),
          field: 'style.color' as any,
          width: 40,
          type: 'color',
          editable: params => isEditable(params, 'color')
        }
      ],
      [t, isEditable]
    )

    // Dynamic columns based on section type
    const specificColumns = useMemo<ColDef<CustomReportVariable>[]>(() => {
      switch (sectionType) {
        case 'table':
          return tableColumns
        case 'graph':
          return chartColumns
        case 'pie':
          return pieColumns
        default:
          return []
      }
    }, [sectionType, tableColumns, chartColumns, pieColumns])

    // Combine all columns
    const columnDefs = useMemo<ColDef<CustomReportVariable>[]>(
      () => [...baseColumns, ...commonColumns, ...specificColumns],
      [baseColumns, commonColumns, specificColumns]
    )

    // Default column definition - applies to all columns
    const defaultColDef = useMemo<ColDef<CustomReportVariable>>(
      () => ({
        sortable: false,
        filter: false,
        suppressMenu: true,
        suppressHeaderContextMenu: true,
        suppressHeaderMenuButton: true,
        resizable: true
      }),
      []
    )

    const getRowId = useCallback((params: GetRowIdParams<CustomReportVariable>) => {
      if (params.data.id != null) {
        return params.data.id.toString()
      }
      // Get or create a stable fallback ID for rows without id
      let fallbackId = fallbackIdMap.get(params.data)
      if (!fallbackId) {
        fallbackIdCounter += 1
        fallbackId = `fallback-row-${fallbackIdCounter}`
        fallbackIdMap.set(params.data, fallbackId)
      }
      return fallbackId
    }, [])

    // Clipboard handlers for array values (e.g. dataType)
    const processCellForClipboard = useCallback((params: ProcessCellForExportParams) => {
      const { value } = params
      // Serialize arrays to JSON for clipboard
      if (Array.isArray(value)) {
        return JSON.stringify(value)
      }
      return value
    }, [])

    const processCellFromClipboard = useCallback((params: ProcessCellForExportParams) => {
      const { value } = params
      // Try to parse JSON arrays from clipboard
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value)
          if (Array.isArray(parsed)) {
            return parsed
          }
        } catch {
          // Not JSON, return as-is
        }
      }
      return value
    }, [])

    // Cell selection with fill handle (vertical only)
    const cellSelection = useMemo(
      () => ({
        handle: {
          mode: 'fill' as const,
          direction: 'y' as const
        }
      }),
      []
    )

    return (
      <div style={{ height: 500, width: '100%' }}>
        <CustomAgGrid
          ref={gridRef}
          rowData={variables}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          onCellValueChanged={onCellValueChanged}
          popupParent={popupParent}
          domLayout="normal"
          headerHeight={32}
          suppressHorizontalScroll={false}
          alwaysShowHorizontalScroll={false}
          alwaysShowVerticalScroll={variables.length > 100}
          suppressColumnVirtualisation={false}
          suppressRowVirtualisation={variables.length <= 100}
          cellSelection={cellSelection}
          processCellForClipboard={processCellForClipboard}
          processCellFromClipboard={processCellFromClipboard}
        />
      </div>
    )
  }
)

export default React.memo(ParamsAndStylesTableAgGrid)
